import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiUrl } from '../../config/api';
import { FaUser, FaUserTie, FaSearch } from 'react-icons/fa';
import './AdminTables.css';

const RECENT_USERS_LIMIT = 5;

const AdminUsers = () => {
  const [guests, setGuests] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const fetchUsers = useCallback(async (search = '', all = false) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const qs = search
        ? `?search=${encodeURIComponent(search)}`
        : all
          ? `?all=true`
          : `?limit=${RECENT_USERS_LIMIT}`;
      const [gRes, hRes] = await Promise.all([
        fetch(getApiUrl(`/api/admin/users/guests${qs}`), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(getApiUrl(`/api/admin/users/hosts${qs}`), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const gJson = await gRes.json();
      const hJson = await hRes.json();
      if (gJson.success) setGuests(gJson.guests || []);
      else setGuests([]);
      if (hJson.success) setHosts(hJson.hosts || []);
      else setHosts([]);
      if (!gJson.success) setError(gJson.message);
      else if (!hJson.success) setError(hJson.message);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers('');
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e?.preventDefault();
    const q = searchInput.trim();
    if (!q) {
      setSearchActive(false);
      setShowAll(false);
      fetchUsers('');
      return;
    }
    setSearchActive(true);
    setShowAll(false);
    fetchUsers(q);
  };

  const handleShowAll = () => {
    setSearchInput('');
    setSearchActive(false);
    setShowAll(true);
    fetchUsers('', true);
  };

  const handleShowRecent = () => {
    setSearchInput('');
    setSearchActive(false);
    setShowAll(false);
    fetchUsers('');
  };

  const displayedUsers = useMemo(() => {
    const combined = [
      ...guests.map((g) => ({
        ...g,
        role: 'Guest',
        detail: `${g.bookingCount ?? 0} bookings`,
      })),
      ...hosts.map((h) => ({
        ...h,
        role: 'Host',
        detail: `${h.propertyCount ?? 0} properties · ₹${(h.totalEarnings ?? 0).toLocaleString('en-IN')}`,
      })),
    ].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    if (searchActive || showAll) return combined;
    return combined.slice(0, RECENT_USERS_LIMIT);
  }, [guests, hosts, searchActive, showAll]);

  if (loading && guests.length === 0 && hosts.length === 0) {
    return (
      <div className="admin-page-loading">
        <div className="admin-spinner" />
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <h2 className="admin-page-title">User Overview</h2>
      <p className="admin-page-subtitle">
        {searchActive
          ? 'Users matching your search across guests and hosts.'
          : showAll
            ? 'All registered users.'
            : `Latest ${RECENT_USERS_LIMIT} registered users.`}
      </p>

      {error && <div className="admin-page-error">{error}</div>}

      <form className="admin-users-search-bar" onSubmit={handleSearch}>
        <input
          type="search"
          className="admin-users-search-input"
          placeholder="Search by name, email, or phone..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search users"
        />
        <button type="submit" className="admin-users-search-btn">
          <FaSearch aria-hidden />
          Search
        </button>
        {!showAll && (
          <button type="button" className="admin-users-recent-btn" onClick={handleShowAll}>
            Show All Users
          </button>
        )}
        {(searchActive || showAll) && (
          <button type="button" className="admin-users-recent-btn" onClick={handleShowRecent}>
            Show Recent {RECENT_USERS_LIMIT}
          </button>
        )}
      </form>

      <h3 className="admin-section-title">
        {searchActive
          ? 'Search Results'
          : showAll
            ? `All Users (${displayedUsers.length})`
            : `Recent Users (${RECENT_USERS_LIMIT})`}
      </h3>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Details</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {displayedUsers.length === 0 ? (
              <tr>
                <td colSpan={6} className="admin-empty-cell">
                  {searchActive ? 'No users match your search.' : 'No recent users found.'}
                </td>
              </tr>
            ) : (
              displayedUsers.map((u) => (
                <tr key={`${u.role}-${u._id}`}>
                  <td>
                    <span className="admin-user-name-cell">
                      {u.role === 'Host' ? (
                        <FaUserTie className="admin-user-role-icon" aria-hidden />
                      ) : (
                        <FaUser className="admin-user-role-icon" aria-hidden />
                      )}
                      {u.name || '—'}
                    </span>
                  </td>
                  <td>{u.email || '—'}</td>
                  <td>{u.phoneNumber || '—'}</td>
                  <td>
                    <span className={`admin-role-badge admin-role-badge--${u.role.toLowerCase()}`}>
                      {u.role}
                    </span>
                  </td>
                  <td>{u.detail}</td>
                  <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminUsers;
